import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Building2, ArrowLeft, ArrowRight, Leaf, CheckCircle2, Loader2, Search } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import StepNavigation from "@/components/StepNavigation";
import { useSiretLookup } from "@/hooks/useSiretLookup";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WORK_TYPES = [
  { id: "isolation_combles", label: "Isolation des combles" },
  { id: "isolation_murs", label: "Isolation des murs" },
  { id: "isolation_planchers", label: "Isolation des planchers" },
  { id: "pompe_chaleur", label: "Pompe à chaleur" },
  { id: "chaudiere", label: "Chaudière performante" },
  { id: "fenetres", label: "Fenêtres / Menuiseries" },
  { id: "ventilation", label: "Ventilation (VMC)" },
  { id: "renovation_globale", label: "Rénovation globale" },
  { id: "eclairage_led", label: "Éclairage LED" },
  { id: "gestion_energie", label: "Gestion de l'énergie (GTB/GTC)" }
];

const SECTORS = [
  { value: "industrie", label: "Industrie" },
  { value: "tertiaire", label: "Tertiaire / Services" },
  { value: "commerce", label: "Commerce" },
  { value: "agriculture", label: "Agriculture" },
  { value: "transport", label: "Transport / Logistique" },
  { value: "batiment", label: "BTP / Construction" },
  { value: "sante", label: "Santé" },
  { value: "hotellerie", label: "Hôtellerie / Restauration" },
  { value: "autre", label: "Autre" }
];

// Définition des étapes pour la navigation
const STEPS = [
  { id: 1, title: "Entreprise" },
  { id: 2, title: "Contact" },
  { id: 3, title: "Adresse" },
  { id: 4, title: "Bâtiment" },
  { id: 5, title: "Projet" }
];

export default function EntrepriseForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { lookupSiret, isLoading: siretLoading } = useSiretLookup();
  const [formData, setFormData] = useState({
    beneficiary_type: "entreprise",
    // Entreprise info
    company_name: "",
    siret: "",
    sector: "",
    employee_count: "",
    annual_revenue: "",
    // Contact
    contact_first_name: "",
    contact_last_name: "",
    email: "",
    phone: "",
    // Adresse
    address: "",
    postal_code: "",
    city: "",
    department: "",
    // Bâtiment
    housing_type: "batiment",
    housing_age: "",
    housing_surface: "",
    // Travaux
    work_types: [],
    // Projet
    urgency: "3_mois",
    has_internal_budget: false,
    decision_maker_validated: false,
    additional_info: ""
  });

  const totalSteps = 5;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Recherche automatique SIRET quand 14 chiffres sont saisis
  const handleSiretChange = async (value) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 14);
    updateField("siret", cleanValue);
    
    // Recherche automatique quand on a 14 chiffres
    if (cleanValue.length === 14) {
      const result = await lookupSiret(cleanValue);
      if (result) {
        setFormData(prev => ({
          ...prev,
          company_name: result.company_name || prev.company_name,
          address: result.address || prev.address,
          postal_code: result.postal_code || prev.postal_code,
          city: result.city || prev.city,
          department: result.department || prev.department
        }));
        toast.success("Informations entreprise récupérées automatiquement !");
      }
    }
  };

  const toggleWorkType = (workId) => {
    setFormData(prev => ({
      ...prev,
      work_types: prev.work_types.includes(workId)
        ? prev.work_types.filter(w => w !== workId)
        : [...prev.work_types, workId]
    }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.company_name || !formData.siret) {
          toast.error("Veuillez remplir le nom de l'entreprise et le SIRET");
          return false;
        }
        if (formData.siret.length !== 14) {
          toast.error("Le SIRET doit contenir 14 chiffres");
          return false;
        }
        return true;
      case 2:
        if (!formData.contact_first_name || !formData.contact_last_name || !formData.email || !formData.phone) {
          toast.error("Veuillez remplir tous les champs de contact");
          return false;
        }
        return true;
      case 3:
        if (!formData.address || !formData.postal_code || !formData.city || !formData.department) {
          toast.error("Veuillez remplir l'adresse complète du bâtiment");
          return false;
        }
        return true;
      case 4:
        if (!formData.housing_age || !formData.housing_surface) {
          toast.error("Veuillez renseigner l'âge et la surface du bâtiment");
          return false;
        }
        if (formData.work_types.length === 0) {
          toast.error("Veuillez sélectionner au moins un type de travaux");
          return false;
        }
        return true;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        housing_age: parseInt(formData.housing_age),
        housing_surface: parseInt(formData.housing_surface),
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        annual_revenue: formData.annual_revenue ? parseInt(formData.annual_revenue) : null
      };

      const response = await fetch(`${API_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de la soumission");
      }

      const lead = await response.json();
      toast.success("Demande enregistrée avec succès !");
      navigate(`/resultat/${lead.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Navbar */}
      <nav className="navbar flex items-center justify-between" data-testid="navbar">
        <Link to="/" className="flex items-center gap-2">
          <Leaf className="h-8 w-8 text-[#1A4D2E]" />
          <span className="text-2xl font-bold text-[#1A4D2E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            MesSubventions
          </span>
        </Link>
        <div className="flex items-center gap-2 text-[#1A4D2E]">
          <Building2 className="h-5 w-5" />
          <span className="font-medium">Entreprise</span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Bar - Navigation cliquable */}
        <div className="mb-8">
          <StepNavigation
            steps={STEPS}
            currentStep={step}
            onStepClick={(s) => setStep(s)}
            className="mb-4"
          />
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#1A4D2E] transition-all duration-300"
              style={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
            />
          </div>
          <p className="text-sm text-slate-500 mt-2 text-center">
            Étape {step} sur {totalSteps}: {STEPS[step - 1].title}
            {step > 1 && (
              <span className="text-[#1A4D2E] ml-2">(cliquez sur une étape précédente pour y revenir)</span>
            )}
          </p>
        </div>

        {/* Form Container - Enveloppé dans <form> pour l'autocomplétion navigateur */}
        <form 
          autoComplete="on"
          className="bg-white rounded-2xl shadow-lg p-8" 
          data-testid="entreprise-form"
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Step 1: Entreprise Info */}
          {step === 1 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Votre entreprise
                </h2>
                <p className="text-slate-600">Entrez votre SIRET pour remplir automatiquement les informations</p>
              </div>

              <div className="space-y-4">
                {/* SIRET avec auto-remplissage */}
                <div>
                  <Label htmlFor="siret">SIRET * (14 chiffres)</Label>
                  <div className="relative">
                    <Input
                      id="siret"
                      value={formData.siret}
                      onChange={e => handleSiretChange(e.target.value)}
                      placeholder="Entrez votre SIRET pour remplir auto les infos"
                      maxLength={14}
                      className="pr-10"
                      data-testid="siret-input"
                    />
                    {siretLoading ? (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1A4D2E] animate-spin" />
                    ) : formData.siret.length === 14 ? (
                      <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    ) : (
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    💡 Les informations de l'entreprise et l'adresse seront remplies automatiquement
                  </p>
                </div>

                <div>
                  <Label htmlFor="company_name">Raison sociale *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={e => updateField("company_name", e.target.value)}
                    placeholder="Nom de votre entreprise"
                    autoComplete="organization"
                    data-testid="company-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="sector">Secteur d'activité</Label>
                  <Select value={formData.sector} onValueChange={v => updateField("sector", v)}>
                    <SelectTrigger data-testid="sector-select">
                      <SelectValue placeholder="Sélectionnez un secteur" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="employee_count">Nombre d'employés</Label>
                    <Input
                      id="employee_count"
                      type="number"
                      value={formData.employee_count}
                      onChange={e => updateField("employee_count", e.target.value)}
                      placeholder="Ex: 50"
                      data-testid="employee-count-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="annual_revenue">CA annuel (€)</Label>
                    <Input
                      id="annual_revenue"
                      type="number"
                      value={formData.annual_revenue}
                      onChange={e => updateField("annual_revenue", e.target.value)}
                      placeholder="Ex: 5000000"
                      data-testid="annual-revenue-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Contact */}
          {step === 2 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact projet
                </h2>
                <p className="text-slate-600">Personne en charge du projet de rénovation</p>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_first_name">Prénom *</Label>
                    <Input
                      id="contact_first_name"
                      value={formData.contact_first_name}
                      onChange={e => updateField("contact_first_name", e.target.value)}
                      placeholder="Prénom"
                      autoComplete="given-name"
                      data-testid="contact-firstname-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_last_name">Nom *</Label>
                    <Input
                      id="contact_last_name"
                      value={formData.contact_last_name}
                      onChange={e => updateField("contact_last_name", e.target.value)}
                      placeholder="Nom"
                      autoComplete="family-name"
                      data-testid="contact-lastname-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email professionnel *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e => updateField("email", e.target.value)}
                    placeholder="contact@entreprise.fr"
                    autoComplete="email"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={e => updateField("phone", e.target.value)}
                    placeholder="06 12 34 56 78"
                    autoComplete="tel"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Adresse */}
          {step === 3 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Localisation du bâtiment
                </h2>
                <p className="text-slate-600">
                  {formData.address ? "Adresse pré-remplie via SIRET (modifiable)" : "Adresse du site à rénover"}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Adresse *</Label>
                  <AddressAutocomplete
                    value={formData.address}
                    onChange={(value) => updateField("address", value)}
                    onSelect={(addressData) => {
                      setFormData(prev => ({
                        ...prev,
                        address: addressData.address,
                        postal_code: addressData.postal_code,
                        city: addressData.city,
                        department: addressData.department
                      }));
                    }}
                    placeholder="Commencez à taper l'adresse du bâtiment..."
                    data-testid="address-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    💡 L'adresse peut être différente du siège social
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Code postal *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={e => updateField("postal_code", e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="69001"
                      maxLength={5}
                      autoComplete="postal-code"
                      data-testid="postal-code-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Ville *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={e => updateField("city", e.target.value)}
                      placeholder="Lyon"
                      autoComplete="address-level2"
                      data-testid="city-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="department">Département *</Label>
                  <Input
                    id="department"
                    value={formData.department}
                    onChange={e => updateField("department", e.target.value.slice(0, 3))}
                    placeholder="69"
                    maxLength={3}
                    data-testid="department-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Bâtiment et Travaux */}
          {step === 4 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Bâtiment et travaux
                </h2>
                <p className="text-slate-600">Caractéristiques du bâtiment et travaux envisagés</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="housing_type">Type de bâtiment *</Label>
                  <Select value={formData.housing_type} onValueChange={v => updateField("housing_type", v)}>
                    <SelectTrigger data-testid="housing-type-select">
                      <SelectValue placeholder="Type de bâtiment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="batiment">Bâtiment industriel / Entrepôt</SelectItem>
                      <SelectItem value="local">Bureau / Local commercial</SelectItem>
                      <SelectItem value="maison">Bâtiment historique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="housing_age">Âge du bâtiment (années) *</Label>
                    <Input
                      id="housing_age"
                      type="number"
                      value={formData.housing_age}
                      onChange={e => updateField("housing_age", e.target.value)}
                      placeholder="Ex: 30"
                      data-testid="housing-age-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="housing_surface">Surface (m²) *</Label>
                    <Input
                      id="housing_surface"
                      type="number"
                      value={formData.housing_surface}
                      onChange={e => updateField("housing_surface", e.target.value)}
                      placeholder="Ex: 500"
                      data-testid="housing-surface-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-3 block">Types de travaux envisagés *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {WORK_TYPES.map(work => (
                      <div
                        key={work.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all
                          ${formData.work_types.includes(work.id)
                            ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                            : 'border-slate-200 hover:border-slate-300'
                          }`}
                        onClick={() => toggleWorkType(work.id)}
                        data-testid={`work-type-${work.id}`}
                      >
                        <Checkbox
                          checked={formData.work_types.includes(work.id)}
                          className="pointer-events-none"
                        />
                        <span className="text-sm">{work.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Projet */}
          {step === 5 && (
            <div className="space-y-6 animate-fade-in">
              <div>
                <h2 className="text-2xl font-bold text-[#1D1D1D] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Votre projet
                </h2>
                <p className="text-slate-600">Informations complémentaires sur votre projet</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="urgency">Délai de réalisation souhaité *</Label>
                  <Select value={formData.urgency} onValueChange={v => updateField("urgency", v)}>
                    <SelectTrigger data-testid="urgency-select">
                      <SelectValue placeholder="Sélectionnez un délai" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediat">Immédiat (moins d'1 mois)</SelectItem>
                      <SelectItem value="3_mois">Dans les 3 prochains mois</SelectItem>
                      <SelectItem value="6_mois">Dans les 6 prochains mois</SelectItem>
                      <SelectItem value="plus_tard">Plus tard / En réflexion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <div
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${formData.has_internal_budget
                        ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                        : 'border-slate-200 hover:border-slate-300'
                      }`}
                    onClick={() => updateField("has_internal_budget", !formData.has_internal_budget)}
                    data-testid="has-budget-checkbox"
                  >
                    <Checkbox
                      checked={formData.has_internal_budget}
                      className="pointer-events-none"
                    />
                    <div>
                      <span className="font-medium">Budget interne alloué</span>
                      <p className="text-sm text-slate-500">Un budget a été provisionné pour ce projet</p>
                    </div>
                  </div>

                  <div
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${formData.decision_maker_validated
                        ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                        : 'border-slate-200 hover:border-slate-300'
                      }`}
                    onClick={() => updateField("decision_maker_validated", !formData.decision_maker_validated)}
                    data-testid="decision-maker-checkbox"
                  >
                    <Checkbox
                      checked={formData.decision_maker_validated}
                      className="pointer-events-none"
                    />
                    <div>
                      <span className="font-medium">Décisionnaire identifié</span>
                      <p className="text-sm text-slate-500">La personne qui validera le projet est identifiée</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="additional_info">Informations complémentaires</Label>
                  <Textarea
                    id="additional_info"
                    value={formData.additional_info}
                    onChange={e => updateField("additional_info", e.target.value)}
                    placeholder="Précisions sur votre projet, contraintes particulières..."
                    rows={4}
                    data-testid="additional-info-textarea"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            {step > 1 ? (
              <Button
                variant="outline"
                onClick={prevStep}
                className="flex items-center gap-2"
                data-testid="prev-step-btn"
              >
                <ArrowLeft className="h-4 w-4" />
                Précédent
              </Button>
            ) : (
              <Link to="/">
                <Button variant="outline" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Retour
                </Button>
              </Link>
            )}

            {step < totalSteps ? (
              <Button
                type="button"
                onClick={nextStep}
                className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 flex items-center gap-2"
                data-testid="next-step-btn"
              >
                Suivant
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90 flex items-center gap-2"
                data-testid="submit-btn"
              >
                {loading ? "Envoi en cours..." : "Calculer mon éligibilité"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
